import { computed } from "vue";
import type { Currency } from "../types/currency-type";
import model from "../model";
import { firstIfUndefined } from "../utils/utils";
import { toCurrency } from "./currency-management";

export type StellarStreamConfigItem = {
    name: string,
    color: string,
    pointsRequired: number,
    cash: Partial<Record<Currency, number>>,
    imgSrc: Record<string, unknown>,
};

export const stellarStreamConfig: StellarStreamConfigItem[] = [
    {
        name: 'vega',
        color: '#2989d8',
        pointsRequired: 300,
        cash: {
            'EUR': 3,
            'INR': 250,
            'PEN': 15,
        },
        imgSrc: import.meta.glob('@/assets/stellar-stream/vega_chip.*', {eager: true, query: '?url', import: 'default'}),
    },
    {
        name: 'arcturus',
        color: '#59ff88',
        pointsRequired: 500,
        cash: {
            'EUR': 10,
            'INR': 800,
            'PEN': 50,
        },
        imgSrc: import.meta.glob('@/assets/stellar-stream/arcturus_chip.*', {eager: true, query: '?url', import: 'default'}),
    },
    {
        name: 'kentaurus',
        color: '#2989d8',
        pointsRequired: 1750,
        cash: {
            'EUR': 50,
            'INR': 4000,
            'PEN': 250,
        },
        imgSrc: import.meta.glob('@/assets/stellar-stream/kentaurus_chip.*', {eager: true, query: '?url', import: 'default'}),
    },
    {
        name: 'canopus',
        color: '#ff4c4f',
        pointsRequired: 2500,
        cash: {
            'EUR': 100,
            'INR': 8000,
            'PEN': 500,
        },
        imgSrc: import.meta.glob('@/assets/stellar-stream/canopus_chip.*', {eager: true, query: '?url', import: 'default'}),
    },
    {
        name: 'sirius',
        color: '#ffd800',
        pointsRequired: 5000,
        cash: {
            'EUR': 250,
            'INR': 20000,
            'PEN': 1250,
        },
        imgSrc: import.meta.glob('@/assets/stellar-stream/sirius_chip.*', {eager: true, query: '?url', import: 'default'}),
    },
]

const points = computed<number>(() => {
    return model.keepAlive?.bonusPoints || 0;
});

const currentLevelInfo = computed<StellarStreamConfigItem | undefined>(() => {
    for(let i = 0, len = stellarStreamConfig.length; i < len; i++){
        const levelInfo = stellarStreamConfig[i];
        const nextLevelInfo = stellarStreamConfig[i + 1];

        if(i === 0 && points.value < levelInfo.pointsRequired) return;
        if(i === len - 1 && points.value >= levelInfo.pointsRequired) return levelInfo;
        if(levelInfo.pointsRequired <= points.value && nextLevelInfo.pointsRequired > points.value) return levelInfo;
    }
});

const nextLevelInfo = computed<StellarStreamConfigItem | undefined>(() => {
    const currLevelInfo = currentLevelInfo.value;
    const nextLevelInfoInd = currLevelInfo ? stellarStreamConfig.indexOf(currLevelInfo) + 1 : 0;
    return stellarStreamConfig[nextLevelInfoInd];
});

const currLevelPosition = computed<number>(() => {
    const currLevelInfo = currentLevelInfo.value;
    return currLevelInfo ? stellarStreamConfig.indexOf(currLevelInfo) : -1;
});

const currLevelCash = computed<string>(() => {
    const cash = currentLevelInfo.value?.cash;
    if(!cash) return '';

    const cashAmount = firstIfUndefined(model.userCurrency, cash);
    if(!cashAmount) return '';

    return toCurrency(cashAmount, {credits: false});
});

const nextLevelCash = computed<string>(() => {
    const cash = nextLevelInfo.value?.cash;
    if(!cash) return '';

    const cashAmount = firstIfUndefined(model.userCurrency, cash);
    if(!cashAmount) return '';

    return toCurrency(cashAmount, {credits: false});
});

const nextLevelPointsLeft = computed<number>(() => {
    const nextLevelRequiredPoints = nextLevelInfo.value?.pointsRequired;
    if(!nextLevelRequiredPoints) return 0;

    return nextLevelRequiredPoints - points.value;
});

const resetDate = computed<string>(() => {
    const date = new Date();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + 1);
    return new Intl.DateTimeFormat(model.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
});

function isLevelEnabled(levelInfo: StellarStreamConfigItem): boolean{
    return currentLevelInfo.value == levelInfo;
}

export default { 
    points, 
    currentLevelInfo, 
    nextLevelInfo, 
    currLevelCash, 
    nextLevelCash, 
    resetDate, 
    nextLevelPointsLeft, 
    currLevelPosition,
    isLevelEnabled,
 }